/**
 * IMPORTS
 */

import { useEffect, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";

import { useStoreZustandSearchShipment } from "@/store-zustand/search-shipment/search-shipment";
import { useStoreZustandUserAuth } from "@/store-zustand/user-auth";

import { handleGetSearchSpnment } from "@/domain/use-cases/search-spnment";
import { getStatusLabel } from "@/utils/get-status-label";

export interface ICliente {
  id: number;
  nome: string;
  email: string;
  cnpj: string;
  cpf: string;
  ativo: boolean;
  tipo_pessoa: string;
  telefone: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

interface ISolicitante {
  id: number;
  remessa_id: number;
  user_id: number;
  nome: string;
  cpf: string;
  telefone: string;
  created_at: string;
  updated_at: string;
}

interface IConsultor {
  id: number;
  nome: string;
  email: string;
  cpf: string;
  telefone: string;
  created_at: string;
  updated_at: string;
}
export interface ITecnologia {
  id: number;
  nome: string;
  descricao: string;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}

export interface IModeloTecnico {
  id: number;
  user_id: number;
  produto_id: number;
  tecnologia_id: number;
  nome_modelo: string;
  tipo_entrega: string;
  posicionamento: string;
  tem_furo: number;
  tem_carga_foto: number;
  tem_dados_variaveis: number;
  campo_chave: string;
  foto_frente_path: string | null;
  foto_verso_path: string | null;
  observacoes: string;
  created_at: string;
  updated_at: string;
}

export interface IRemessa {
  id: number;
  total_solicitacoes: number;
  situacao: string;
  data_remessa: string;
  data_inicio_producao: string | Date;
  data_fim_producao: string | Date;
  posicao: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  cliente: ICliente;
  tecnologia: ITecnologia;
  modelo_tecnico: IModeloTecnico;
  solicitante: ISolicitante;
  consultor?: IConsultor; // opcional, você usa no map
  designer: any;
  responsavel?: string; // idem
  observacao: string;
  numero_remessa: string;
}

export interface ISearchShipmentResponse {
  code: number;
  status: string;
  message: string;
  data: IRemessa[];
  pagination: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export function generatePagination(
  currentPage: number,
  lastPage: number,
  delta = 2
): (number | string)[] {
  const range: (number | string)[] = [];
  const left = Math.max(2, currentPage - delta);
  const right = Math.min(lastPage - 1, currentPage + delta);

  range.push(1);

  if (left > 2) {
    range.push("...");
  }

  for (let i = left; i <= right; i++) {
    range.push(i);
  }

  if (right < lastPage - 1) {
    range.push("...");
  }

  if (lastPage > 1) {
    range.push(lastPage);
  }

  return range;
}

function useManageSearchShipment() {
  const { currentPage, itemsPerPage, searchItem, setTotalItemsPage, setPaginations, setLastPage } =
    useStoreZustandSearchShipment();

  const { token: accessToken } = useStoreZustandUserAuth();

  // Chave de cache única baseada na página atual e na quantidade de itens por página
  const queryKey = ["search-shipment", currentPage, itemsPerPage, searchItem];

  const {
    error: isError,
    data: remessasResponse,
    isLoading: isLoadingMain,
  } = useQuery<ISearchShipmentResponse>({
    queryKey,
    queryFn: async () => {
      const response = (await handleGetSearchSpnment(
        accessToken!,
        currentPage,
        itemsPerPage,
        searchItem!
      )) as any;

      return response;
    },
  });

  const searchShipments = remessasResponse?.data ?? [];

  const pagination = remessasResponse?.pagination;

  const rows = searchShipments?.map((remessa) => ({
    id: remessa.id,
    numero_remessa: remessa?.numero_remessa ?? "N/A",
    situacao: remessa?.situacao ?? "N/A",
    client_id: remessa?.cliente?.nome ?? "N/A",
    criado_por: remessa?.cliente?.nome ?? "N/A",
    status: getStatusLabel(remessa?.situacao) ?? "N/A",
    quantidade: remessa?.total_solicitacoes ?? 0,
    modelo: remessa?.modelo_tecnico?.nome_modelo ?? "N/A",
    consultor: remessa?.consultor?.nome ?? "N/A", // campo não existe no JSON, placeholder
    responsavel: remessa?.designer?.nome ?? "N/A", // idem
    nome: remessa?.cliente?.nome || "N/A",
    data: remessa?.created_at ? new Date(remessa?.created_at).toLocaleString("pt-BR") : "N/A",
    data_inicio_producao: remessa?.data_inicio_producao
      ? new Date(remessa?.data_inicio_producao).toLocaleString("pt-BR")
      : "N/A",
    data_fim_producao: remessa?.data_fim_producao
      ? new Date(remessa?.data_fim_producao).toLocaleString("pt-BR")
      : "N/A",
    observacao: remessa?.observacao ?? "",
    hasMensagem: Boolean(remessa?.observacao !== null),
    solicitante: remessa?.solicitante?.nome ?? "N/A",
  }));

  // ✅ Só recalcula se currentPage ou last_page mudar
  const pages = useMemo(() => {
    return generatePagination(currentPage, pagination?.last_page ?? 1);
  }, [currentPage, pagination?.last_page]);

  // ✅ Atualiza Zustand uma única vez
  useEffect(() => {
    setPaginations(pages as number[]);
    setLastPage(Number(pagination?.last_page));
    setTotalItemsPage(Number(pagination?.total));
  }, [pages, setPaginations, setLastPage, pagination, setTotalItemsPage]);

  return {
    searchShipments: rows ?? [],
    paginations: pages,
    isLoadingMain,
    isError,
  };
}

/**
 * EXPORTS
 */
export { useManageSearchShipment };
